import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Globe } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const LanguageSwitcher = ({ variant = 'header' }) => {
  const { currentLanguage, changeLanguage, languages } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const currentLang = languages.find(l => l.code === currentLanguage) || languages[0];

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (langCode) => {
    changeLanguage(langCode);
    setIsOpen(false);
  };

  const isFooter = variant === 'footer';

  return (
    <div 
      ref={dropdownRef} 
      style={{ 
        position: 'relative',
        display: 'inline-block'
      }}
    >
      <button
        onClick={() => setIsOpen(!isOpen)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: isFooter ? '8px 12px' : '8px 16px',
          background: isFooter ? 'var(--bg-primary)' : 'rgba(255, 255, 255, 0.05)',
          border: '1px solid var(--border-subtle)',
          borderRadius: '8px',
          color: 'var(--text-secondary)',
          fontSize: '14px',
          fontWeight: 500,
          cursor: 'pointer',
          transition: 'all 0.2s ease'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = isFooter ? 'var(--bg-secondary)' : 'rgba(255, 255, 255, 0.1)';
          e.currentTarget.style.borderColor = 'var(--accent-gold)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = isFooter ? 'var(--bg-primary)' : 'rgba(255, 255, 255, 0.05)';
          e.currentTarget.style.borderColor = 'var(--border-subtle)';
        }}
      >
        <Globe size={16} />
        <span>{currentLang.flag}</span>
        <span>{currentLang.code.toUpperCase()}</span>
        <ChevronDown 
          size={14} 
          style={{ 
            transform: isOpen ? 'rotate(180deg)' : 'rotate(0)',
            transition: 'transform 0.2s ease'
          }} 
        />
      </button>

      {isOpen && (
        <div
          style={{
            position: 'absolute',
            top: isFooter ? 'auto' : '100%',
            bottom: isFooter ? '100%' : 'auto',
            right: 0,
            marginTop: isFooter ? 0 : '8px',
            marginBottom: isFooter ? '8px' : 0,
            background: 'var(--bg-secondary)',
            border: '1px solid var(--border-primary)',
            borderRadius: '12px',
            padding: '8px',
            minWidth: '180px',
            maxHeight: '300px',
            overflowY: 'auto',
            zIndex: 1000,
            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.5)'
          }}
        >
          {languages.map((lang) => (
            <button
              key={lang.code}
              onClick={() => handleSelect(lang.code)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                width: '100%',
                padding: '10px 12px',
                background: currentLanguage === lang.code ? 'rgba(212, 175, 55, 0.1)' : 'transparent',
                border: 'none',
                borderRadius: '8px',
                color: currentLanguage === lang.code ? 'var(--accent-gold)' : 'var(--text-secondary)',
                fontSize: '14px',
                fontWeight: currentLanguage === lang.code ? 600 : 400,
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                textAlign: 'left'
              }}
              onMouseEnter={(e) => {
                if (currentLanguage !== lang.code) {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
                }
              }}
              onMouseLeave={(e) => {
                if (currentLanguage !== lang.code) {
                  e.currentTarget.style.background = 'transparent';
                }
              }}
            >
              <span style={{ fontSize: '18px' }}>{lang.flag}</span>
              <span>{lang.name}</span>
              {currentLanguage === lang.code && (
                <span style={{ marginLeft: 'auto', color: 'var(--accent-gold)' }}>✓</span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default LanguageSwitcher;
